<script>
  import { onMount } from "svelte"
  import BadgeState from "../../Stores/badgeState"
  import { sleep } from "../../../../../Script/sleep"
  import notify from "../../../../../Script/notify"

  /**
   * @type {HTMLCanvasElement}
   */
  export let canvas
  export let cropCanvas = false
  export let showCropLoader = false
  export let loadingText

  let closeNotification = {
    close() {},
  }
  let canvasScale
  /**
   * @type {CanvasRenderingContext2D}
   */
  let ctx
  let dragging = false
  let selectedObject = null
  let offsetX = 0
  let offsetY = 0
  const textPadding = 1.3
  const textPaddingOffset = textPadding * 0.5

  onMount(() => {
    ctx = canvas.getContext("2d")
    ctx.willReadFrequently = true

    canvas.width = $BadgeState.baseInfo.width
    canvas.height = $BadgeState.baseInfo.height

    canvasScale = $BadgeState.baseInfo.width / canvas.clientWidth
    $BadgeState.scale = 1

    window.requestAnimationFrame(watcher)
    $BadgeState.activeFontSize = Math.floor($BadgeState.baseInfo.height * (20 / 100))

    BadgeState.subscribe((state) => {
      window.requestAnimationFrame(watcher)
    })
  })

  function applyCanvasBgColor(ctx, canvas, canvasColor, type) {
    try {
      // Check if the canvasColor is a linear gradient
      if (canvasColor.startsWith("linear-gradient")) {
        // Extract the angle and colors from the linear-gradient string
        const linearGradientRegex = /linear-gradient\((\d+)deg, (.+)\)/
        const match = canvasColor.match(linearGradientRegex)

        if (!match) return

        const angle = parseInt(match[1])
        const colorStops = match[2].split(",")

        // Calculate the end coordinates for the gradient based on the angle
        const radians = (angle * Math.PI) / 180
        const x1 = canvas.width * Math.cos(radians)
        const y1 = canvas.height * Math.sin(radians)

        // Create the linear gradient
        const gradient = ctx.createLinearGradient(0, 0, x1, y1)

        // Add color stops (strip percentages from color strings)
        colorStops.forEach((stop, index) => {
          const cleanedStop = stop.trim().replace(/ \d+%$/, "") // Remove percentage
          gradient.addColorStop(index / (colorStops.length - 1), cleanedStop)
        })

        // Apply the gradient to the canvas
        ctx.fillStyle = gradient
      } else {
        // Assume it's a solid color and apply directly
        if (canvasColor == "transparent") {
          // Clear the canvas
          ctx.clearRect(0, 0, $BadgeState.baseInfo.width, $BadgeState.baseInfo.height)
        } else {
          ctx.fillStyle = canvasColor
        }
      }
    } catch (error) {
      console.error(error)
      throw error
    }
  }

  const watcher = async () => {
    try {
      if (!canvas) return
      // $BadgeState.baseInfo.width = canvas.width
      // $BadgeState.baseInfo.height = canvas.height
      // ctx.globalAlpha = 1
      applyCanvasBgColor(ctx, canvas, $BadgeState.canvasColor)
      if ($BadgeState.canvasColor != "transparent" || $BadgeState.download) ctx.fillRect(0, 0, $BadgeState.baseInfo.width, $BadgeState.baseInfo.height)

      // ctx.beginPath()
      // ctx.moveTo($BadgeState.baseInfo.width / 2, 0)
      // ctx.lineTo($BadgeState.baseInfo.width / 2, $BadgeState.baseInfo.height)

      // ctx.moveTo(0, $BadgeState.baseInfo.height / 2)
      // ctx.lineTo($BadgeState.baseInfo.width, $BadgeState.baseInfo.height / 2)
      // ctx.stroke()

      // Loop through the operations and render each
      for (let i = 0; i < $BadgeState.operations.length; i++) {
        if ($BadgeState.operations[i].type === "text") {
          if ($BadgeState.operations[i].radius != 0) {
            await renderRoundedText($BadgeState.operations[i])
          } else {
            await renderText($BadgeState.operations[i])
          }
        } else if ($BadgeState.operations[i].type === "image") {
          await renderImage($BadgeState.operations[i])
        } else {
          await renderShape($BadgeState.operations[i])
        }
      }
    } catch (error) {
      console.error(error)
    }
  }

  const renderImage = (layer) => {
    try {
      if (!layer.image) {
        console.error("Image is missing in the layer", layer)
        return
      }

      if (!(layer.image instanceof HTMLImageElement)) {
        console.error("Invalid image type:", layer.image)
        return
      }
      ctx.save()
      ctx.translate(layer.x, layer.y) // Move to the image center

      ctx.rotate((layer.rotation * Math.PI) / 180)

      ctx.translate(-layer.x, -layer.y) // Move to the image center

      // Step 3: Draw the image, aligning it correctly
      ctx.drawImage(layer.image, layer.x - layer.width / 2, layer.y - layer.height / 2, layer.width, layer.height)

      ctx.restore()
    } catch (error) {
      console.error("Error drawing image:", error)
    }
  }

  const renderShape = (layer) => {
    return new Promise((resolve) => {
      const actualWidth = parseFloat(layer.id.split("|")[2])
      const actualHeight = parseFloat(layer.id.split("|")[3])

      layer.data.forEach((pathData) => {
        const path = new Path2D(pathData.d)
        ctx.save()

        let scaleX = layer.width / actualWidth //0.4
        let scaleY = layer.height / actualHeight //0.4

        // Step 1: Move to the center of the shape
        ctx.translate(layer.x, layer.y)

        // Step 2: Rotate first
        ctx.rotate((layer.rotation * Math.PI) / 180)

        // Step 3: Scale but keep the shape centered
        ctx.scale(scaleX, scaleY)

        // Step 4: Move the shape back to align correctly
        ctx.translate(-actualWidth / 2, -actualHeight / 2)

        // Step 5: Draw the shape
        ctx.fillStyle = pathData.fill
        ctx.fill(path)

        ctx.restore()
      })
      if (layer.active) {
        ctx.lineWidth = 3
        ctx.strokeStyle = "rgba(56, 189, 248, 0.6)"
        ctx.strokeRect(layer.x - layer.width / 2 - 10, layer.y - layer.height / 2 - 10, layer.width + 20, layer.height + 20)
      }
      resolve()
    })
  }

  const renderRoundedText = (data) => {
    return new Promise((resolve) => {
      ctx.save()

      let fontWeight = data.fontWeight.toString().replaceAll(",", " ").replace("Underline", " ")
      ctx.font = `${fontWeight} ${data.fontSize}px '${data.fontFamily}'`
      ctx.fillStyle = data.textColor
      ctx.textAlign = data.textAlign

      const startAngle = -90 * (Math.PI / 180)
      const offset = data.rotation * (Math.PI / 180)
      const endAngle = (360 - 90) * (Math.PI / 180)
      const angleRange = endAngle + offset - startAngle

      // Calculate the angle per character dynamically based on letterSpacing
      const anglePerChar = data.letterSpacing / data.radius

      ctx.translate(data.x, data.y)
      ctx.rotate(startAngle + offset)

      for (let i = 0; i < data.value.length; i++) {
        if (offset + anglePerChar * i < angleRange) {
          ctx.save()
          ctx.rotate(anglePerChar * i)
          ctx.translate(0, -data.radius)
          ctx.fillText(data.value[i], 0, 0)

          // Draw underline with spacing
          if (data.fontWeight.includes("Underline")) {
            const charWidth = ctx.measureText(data.value[i]).width
            const underlineOffset = data.fontSize / 5 // Adjust spacing here
            ctx.beginPath()
            ctx.moveTo(-charWidth / 2, underlineOffset)
            ctx.lineTo(charWidth / 2, underlineOffset)
            ctx.strokeStyle = data.textColor
            ctx.lineWidth = 2
            ctx.stroke()
          }

          ctx.restore()
        } else {
          break
        }
      }

      ctx.restore()
      resolve()
    })
  }

  // const radiansToDegrees = (radians) => {
  //   console.log(radians * (180 / Math.PI))
  // }

  const renderText = (data) => {
    return new Promise((resolve) => {
      ctx.save() // Save the current state

      // Move to the text position first
      ctx.translate(data.x, data.y)

      // Apply rotation
      ctx.rotate((data.rotation * Math.PI) / 180)

      // Set the font properties
      let fontWeight = data.fontWeight.toString().replaceAll(",", " ")
      fontWeight = fontWeight.replace("Underline", " ") // Remove "Underline"
      ctx.font = `${fontWeight} ${data.fontSize}px '${data.fontFamily}'`

      // Set text styles
      ctx.fillStyle = data.textColor
      ctx.textAlign = data.textAlign
      ctx.textBaseline = "middle"

      // Draw the text at (0, 0) since we already moved to the correct position
      ctx.fillText(data.value, 0, 0)
      // ctx.fillText(data.value, data.x, data.y)

      if (data.active) {
        ctx.fillStyle = "rgb(56, 189, 248, 0.2)"
        let x = 0
        let width = ctx.measureText(data.value).width
        let height = data.fontSize * textPadding

        if (data.textAlign == "left") {
          x = 0
        } else if (data.textAlign == "center") {
          x = -width / 2
        } else if (data.textAlign == "right") {
          x = -width
        }

        ctx.fillRect(x, -data.fontSize * textPaddingOffset, width, height)
      }

      // Draw underline if needed
      if (data.fontWeight.includes("Underline")) {
        let { width, actualBoundingBoxDescent } = ctx.measureText(data.value)
        let underlineY = actualBoundingBoxDescent + 2 // Adjust position

        if (data.textAlign === "left") {
          ctx.fillRect(0, underlineY, width, 3)
        } else if (data.textAlign === "center") {
          ctx.fillRect(-width / 2, underlineY, width, 3)
        } else if (data.textAlign === "right") {
          ctx.fillRect(-width, underlineY, width, 3)
        }
      }

      ctx.restore() // Restore the state
      resolve()
    })
  }

  // let clickedElementType = null

  // const onMouseDown = (event) => {
  //   let mouseX = event.offsetX * canvasScale
  //   let mouseY = event.offsetY * canvasScale

  //   // Start checking layers from top to bottom
  //   for (let i = $BadgeState.operations.length - 1; i >= 0; i--) {
  //     let operation = $BadgeState.operations[i]

  //     // Handle shapes
  //     if (operation.active) {
  //       selectedObject = operation // Select this shape
  //       dragging = true
  //       offsetX = mouseX - operation.x
  //       offsetY = mouseY - operation.y
  //       return
  //     }
  //   }
  // }
  const onMouseDown = (event) => {
    console.log(event.offsetX)

    let mouseX = event.offsetX * canvasScale
    let mouseY = event.offsetY * canvasScale

    console.log(mouseX, mouseY)

    hideActive() // Reset the active state for all layers

    // Start checking layers from top to bottom
    for (let i = $BadgeState.operations.length - 1; i >= 0; i--) {
      const operation = $BadgeState.operations[i]

      // Handle shapes
      if (operation.type === "shape") {
        const withinX = mouseX >= operation.x && mouseX <= operation.x + operation.scale * 400
        const withinY = mouseY >= operation.y && mouseY <= operation.y + operation.scale * 400

        if (withinX && withinY) {
          selectedObject = operation // Select this shape
          operation.active = true // Activate the shape
          dragging = true

          offsetX = mouseX - operation.x
          offsetY = mouseY - operation.y

          return // **Stop further checks once a shape is selected**
        }
      }

      // Handle text layers
      if (operation.type === "text" || operation.move) {
        if (operation.radius != 0) {
          // **Condition for rounded text**
          const distanceFromCenter = Math.sqrt(Math.pow(mouseX - operation.x, 2) + Math.pow(mouseY - operation.y, 2))
          // const radius = 100 // Assuming the radius is the same as in the `renderRoundedText` function
          const textHeight = operation.fontSize

          // Check if the mouse is near the circle where the text is drawn
          const withinRadius = distanceFromCenter >= operation.radius - textHeight && distanceFromCenter <= operation.radius + textHeight

          if (withinRadius) {
            selectedObject = operation // Select this text
            operation.active = true // Activate text layer
            dragging = true

            // Store offsets for dragging
            offsetX = mouseX - operation.x
            offsetY = mouseY - operation.y

            return // Stop further checks
          }
        } else {
          const textWidth = ctx.measureText(operation.value).width
          const textHeight = operation.fontSize

          const withinX = mouseX >= operation.x - textWidth / 2 && mouseX <= operation.x + textWidth / 2
          console.log(operation.x - textWidth / 2, operation.x + textWidth / 2)

          const withinY = mouseY >= operation.y - textHeight / 2 && mouseY <= operation.y + textHeight / 2

          console.log(withinX, withinY)
          if (withinX && withinY) {
            selectedObject = operation // Select this text
            operation.active = true // Activate text layer
            dragging = true

            offsetX = mouseX - operation.x
            offsetY = mouseY - operation.y

            return // **Stop further checks once a text layer is selected**
          }
        }
      }
    }
  }

  const hideActive = () => {
    $BadgeState.operations.forEach((operation) => {
      operation.active = false
    })
  }

  const onMouseMove = (event) => {
    let mouseX = event.offsetX * canvasScale
    let mouseY = event.offsetY * canvasScale

    $BadgeState.cursorPosition.x = Math.round(mouseX)
    $BadgeState.cursorPosition.y = Math.round(mouseY)

    if (!dragging || !selectedObject) return
    selectedObject.x = mouseX - offsetX
    selectedObject.y = mouseY - offsetY

    $BadgeState.operations.map((operation) => {
      if (operation.active) {
        operation.inputY = $BadgeState.baseInfo.height - operation.y
      }
      return operation
    })

    window.requestAnimationFrame(watcher)
  }

  // Download section
  $: if ($BadgeState.download) {
    const a = document.createElement("a")
    a.href = canvas.toDataURL("image/png", 1)
    a.download = `${$BadgeState.name}_${$BadgeState.baseInfo.id}`
    a.click()
    $BadgeState.download = false
  }

  let elmnt
  const resetScale = (scale) => {
    if (scale == 1 && elmnt) {
      elmnt.style.left = 0
      elmnt.style.top = 0
    }
  }

  $: resetScale($BadgeState.scale)
  const stopMove = () => {
    $BadgeState.operations.map((opp) => {
      opp.move = false
      return opp
    })
  }

  $: checkCropping(cropCanvas)

  // for gradient clr
  let tempCanvasColor

  const checkCropping = async () => {
    if (cropCanvas) {
      await onHandleCrop()
      cropCanvas = false
    }
  }

  const onHandleCrop = async () => {
    try {
      showCropLoader = true
      loadingText = "Cropping Badge"
      await sleep(1000)
      tempCanvasColor = $BadgeState.canvasColor

      let TIMEOUT = 0
      if ($BadgeState.canvasColor.startsWith("linear-gradient")) {
        $BadgeState.canvasColor = "#fff"
        TIMEOUT = 1000
      }
      setTimeout(() => {
        const data = handleCrop()
        if (!data) {
          showCropLoader = false
          cropCanvas = false
          $BadgeState.canvasColor = tempCanvasColor
          return
        }
        $BadgeState.canvasColor = tempCanvasColor
        $BadgeState.baseInfo.width = canvas.width = data.width
        $BadgeState.baseInfo.height = canvas.height = data.height
        $BadgeState.operations = $BadgeState.operations.map((layer) => {
          // for layer rearrangement
          layer.x -= data.x
          layer.y -= data.y
          return layer
        })
        showCropLoader = false
        loadingText = "Fetching Badge Data"
      }, TIMEOUT)
    } catch (error) {
      console.error(error)
    }
  }

  let tolerance = 30

  const isBackground = (pixel) => {
    let bgColor = ctx.getImageData(0, 0, 1, 1).data

    let alpha = pixel[3] // Alpha value

    //Transparent Background
    if (alpha === 0) return true

    //Solid Background
    return Math.abs(pixel[0] - bgColor[0]) < tolerance && Math.abs(pixel[1] - bgColor[1]) < tolerance && Math.abs(pixel[2] - bgColor[2]) < tolerance
  }

  const handleCrop = () => {
    try {
      if (!ctx || !canvas) return

      let minX = canvas.width,
        minY = canvas.height,
        maxX = 0,
        maxY = 0
      let found = false

      // Step 1: Find minY & maxY (vertical scan)
      for (let y = 0; y < canvas.height; y++) {
        for (let x = 0; x < canvas.width; x++) {
          let pixel = ctx.getImageData(x, y, 1, 1).data
          if (!isBackground(pixel)) {
            if (!found) {
              // First non-background pixel found, set minY
              minY = y
              found = true
            }
            // update maxY
            maxY = y
            break
          }
        }
      }

      if (!found) {
        closeNotification.close()
        const info = "PAGE: Badge Template Editor\nEVENT: While cropping badge area"
        closeNotification = notify.danger("No area to crop because the background is transparent.", info)
        return false
      }
      found = false

      //Step 2: Find minX & maxX (horizontal scan)
      for (let x = 0; x < canvas.width; x++) {
        for (let y = minY; y <= maxY; y++) {
          let pixel = ctx.getImageData(x, y, 1, 1).data

          if (!isBackground(pixel)) {
            if (!found) {
              minX = x
              found = true
            }
            maxX = x
            break
          }
        }
      }

      let cropWidth = maxX - minX + 1
      let cropHeight = maxY - minY + 1

      return { x: minX, y: minY, width: cropWidth, height: cropHeight }
    } catch (error) {
      console.error(error)
      throw error
    }
  }
</script>

<div bind:this={elmnt} class="absolute inset-0 flex h-full w-full select-none items-center justify-center overflow-hidden px-12 pb-12 pt-5">
  <canvas
    on:wheel|preventDefault|stopPropagation={(e) => {
      if (e.deltaY < 0) {
        if ($BadgeState.scale < 2.5) {
          $BadgeState.scale = $BadgeState.scale + 0.1
        }
      } else {
        if ($BadgeState.scale > 1 && $BadgeState.scale < 3) {
          $BadgeState.scale = $BadgeState.scale - 0.1
        } else $BadgeState.scale = 1
      }
    }}
    on:mousedown={onMouseDown}
    on:mouseup={() => {
      dragging = false
      selectedObject = null
    }}
    on:mousemove={onMouseMove}
    on:click={stopMove}
    id="badgeCanvas"
    bind:this={canvas}
    style="scale:{$BadgeState.scale}; {$BadgeState.canvasColor == 'transparent' ? 'background:url("assets/img/grid.png");' : ''};"
    class="max-h-[650px] cursor-all-scroll overflow-hidden shadow-[0_15px_60px_-15px_rgba(0,0,0,0.3)]"
  />
</div>

<!-- class="max-h-[250px] max-w-[400px] cursor-all-scroll overflow-hidden rounded-md" -->
